import { TuiSkeleton } from '@taiga-ui/kit';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OutputEmitterRef,
  signal,
} from '@angular/core';
import { AsyncPipe, NgClass } from '@angular/common';
import { RequestFormDevicesRequestDeviceAddingComponent } from './components/request-form-devices-request-device-adding/request-form-devices-request-device-adding.component';
import { BehaviorSubject } from 'rxjs';
import { RequestService } from '@core/services/request/request.service';
import { RequestFormDevicesDeviceComponent } from './components/request-form-devices-device/request-form-devices-device.component';
import { RequestDevice } from '@core/types/RequestDevice';
import { Nullable } from '@core/helpers/nullable';
import { RequestFormDevicesRequestDeviceUpdateComponent } from './components/request-form-devices-request-device-update/request-form-devices-request-device-update.component';
import { IsLoadingService } from '@core/services';
import { RequestDevicesService } from '@core/services/request/request-devices.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { EmptyBlockComponent } from '@core/components/empty-block';
import { DeviceInstallQualityCheckStatus } from '@core/types/DeviceInstallQuality';

@Component({
  selector: 'x-request-form-devices',
  imports: [
    TuiSkeleton,
    AsyncPipe,
    RequestFormDevicesRequestDeviceAddingComponent,
    RequestFormDevicesDeviceComponent,
    RequestFormDevicesRequestDeviceUpdateComponent,
    NgClass,
    TranslatePipe,
    EmptyBlockComponent,
  ],
  templateUrl: './request-form-devices.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormDevicesComponent {
  readonly $isInsideSummary = input<boolean>(false, {
    alias: 'isInsideSummary',
  });
  readonly $toggleAddDeviceForm = input<OutputEmitterRef<void>>(undefined, {
    alias: 'toggleAddDeviceForm',
  });

  readonly $requestDevicesInput = input<Nullable<RequestDevice[]>>(null, {
    alias: 'requestDevices',
  });

  readonly $devicesInstallQualityInput = input<
    Nullable<
      Record<RequestDevice['deviceHash'], DeviceInstallQualityCheckStatus>
    >
  >(undefined, { alias: 'devicesInstallQuality' });

  private readonly requestService = inject(RequestService);
  private readonly requestDevicesService = inject(RequestDevicesService);
  private readonly destroyRef = inject(DestroyRef);

  readonly $request = this.requestService.$request;
  readonly isLoading$: BehaviorSubject<boolean> = inject(IsLoadingService);
  public isInitialLoad = true;
  public requestDevices: RequestDevice[] = [];

  constructor() {
    effect(() => {
      if (!this.$isInsideSummary() && this.$request().id) {
        this.initRequestDevices();
      }
    });

    effect(() => {
      const currentDevices = this.$requestDevicesInput();

      if (this.$isInsideSummary() && currentDevices) {
        if (this.isInitialLoad) {
          this.isInitialLoad = false;
        }
        this.requestDevices = currentDevices;
      }
    });
  }

  $activeRequestDeviceEdition = signal<Nullable<RequestDevice['id']>>(null);

  initRequestDevices() {
    this.requestDevicesService.entities$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(devices => {
        if (this.isInitialLoad) {
          this.isInitialLoad = false;
        }
        this.requestDevices = devices;
      });
  }

  onRequestDeviceUpdated() {
    this.requestService.reloadRequest();
    this.requestDevicesService.refreshEntities();
  }

  onRequestDeviceRemoved() {
    this.requestService.reloadRequest();
    this.requestDevicesService.refreshEntities();
  }

  showRequestDeviceEditForm(requestDevice: RequestDevice) {
    this.$activeRequestDeviceEdition.set(requestDevice.id);
  }

  onUpdateFormClosed(wasDeviceUpdated: boolean) {
    this.$activeRequestDeviceEdition.set(null);
    if (wasDeviceUpdated) {
      this.onRequestDeviceUpdated();
    }
  }

  onAddingFormClosed(wasDeviceAdded: boolean) {
    if (wasDeviceAdded) {
      this.$activeRequestDeviceEdition.set(null);
      this.requestDevicesService.refreshEntities();
    }
  }
}
